import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { fetchCategory, fetchCategoryTree } from "../../api/categories";
import ProductCard from "../product/ProductCard";
import SkeletonGrid from "../common/SkeletonGrid";
import Reveal from "../common/Reveal";
import AgeGate from "../common/AgeGate";
import { useAuth } from "../../contexts/AuthContext";
import { isAgeVerifiedAdult } from "../../utils/ageEligibility";
import { API_BASE as API, cachedFetch } from "../../api/api";
import { categoryProductSlugs } from "../../utils/catalogueSeo";
import { useSEO, BASE_URL } from "../../utils/seo";
import { trackFilterUse, trackCategoryView } from "../../utils/analytics";
import "./CategoryPage.css";


/* Featured picks for categories without API products */
const COMING_SOON_TAGLINES = {
  "electronics":    "Phones, TVs, cameras and more — launching soon",
  "phones-tablets": "Latest smartphones and tablets at unbeatable prices",
  "computers":      "Laptops, desktops, peripherals — coming soon",
  "beauty-health":  "Skincare, hair care, health supplements",
  "home-living":    "Furniture, décor, and everything home",
  "appliances":     "Fridges, washers, microwaves and cookware for every kitchen and home",
  "bags":           "Backpacks, totes, travel bags and more",
  "sports-outdoors":"Gear for every sport and outdoor adventure",
  "baby-kids":      "Everything your little ones need",
  "vehicles":       "Car accessories, parts, and more",
  "food-groceries": "Fresh and packaged groceries delivered",
  "drinks":         "Wines, spirits, beers and soft drinks — launching soon",
  "agriculture":    "Seeds, fertilisers, farm tools — serving Kenyan farmers",
  "solar-energy":   "Solar panels, lamps, inverters for off-grid Kenya",
  "books-stationery":"Textbooks, novels, office supplies",
  "gaming":         "Consoles, games, accessories",
  "industrial":     "Tools, safety equipment, electrical supplies",
  "pets":           "Food, accessories, veterinary supplies",
  "travel-services":"Bus tickets, hotel bookings, safari packages",
  "property":       "Apartments, houses, land listings",
  "jewellery-watches":"Fine jewellery and watches",
  "fashion-women":  "Dresses, tops, shoes and accessories for women",
  "fashion-kids":   "Stylish clothing for boys and girls",
};

// Title and description for a category page, in ONE place: this module for
// the rendered app, and netlify/edge-functions/seo.js for the first HTML
// response. The description names 2-3 REAL subcategories rather than
// generic filler, which is what makes one category's snippet different from
// another's — the whole site previously shared a single description.
export const categoryTitle = (cat, activeSub) =>
  activeSub ? `${activeSub.name} – ${cat.name} in Kenya` : `${cat.name} – Shop Online in Kenya`;

export const categoryDescription = (cat, activeSub, children = []) => {
  const names = (children || []).map((c) => c.name).filter(Boolean).slice(0, 3);
  const subject = activeSub ? `${activeSub.name} in ${cat.name}` : cat.name;
  const list = !activeSub && names.length
    ? `: ${names.join(", ")}`
    : "";
  return `Shop ${subject} on Emporia${list}. Pay securely with M-Pesa and get delivery across Kenya.`;
};

const CategoryPage = ({ onAddToCart }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const subSlug = searchParams.get("sub") || "";

  const [cat, setCat]           = useState(null);
  const [catLoading, setCatLoading] = useState(true);
  const [allCats, setAllCats]   = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [retryVersion, setRetryVersion] = useState(0);
  const [categoryError, setCategoryError] = useState(false);
  const [productError, setProductError] = useState(false);
  const [sort, setSort]         = useState("newest");
  const [notified, setNotified] = useState(false);
  // Real account state (is the declared age_range an adult bracket), not a
  // same-tab self-attestation — the backend already enforces this exact
  // boundary (_is_age_verified_adult), so a client-side flag with no
  // account backing would just show content the server refuses to serve.
  // ageJustConfirmed covers the moment right after AgeGate's onConfirm
  // fires, before refreshUser()'s updated `user` has propagated down.
  const [ageJustConfirmed, setAgeJustConfirmed] = useState(false);

  // include_hidden=true so a subcategory that isn't launch_active yet still
  // resolves when linked to directly (?sub=...) — only the chip nav below
  // filters back down to launch_active-only.
  useEffect(() => {
    let cancelled = false;
    setCatLoading(true);
    setCat(null);
    setCategoryError(false);
    fetchCategory(id, true)
      .then((data) => { if (!cancelled) { setCat(data); setCatLoading(false); } })
      .catch((error) => { if (!cancelled) { setCat(null); setCategoryError(error.status !== 404); setCatLoading(false); } });
    return () => { cancelled = true; };
  }, [id, retryVersion]);

  useEffect(() => {
    fetchCategoryTree().then(setAllCats).catch(() => setAllCats([]));
  }, []);

  const visibleChildren = useMemo(() => (cat?.children || []).filter((c) => c.launch_active), [cat]);
  const activeSub = useMemo(() => (cat?.children || []).find((c) => c.slug === subSlug) || null, [cat, subSlug]);

  // Always include the category's own taxonomy id alongside any legacy
  // category values, so products saved against the new taxonomy (e.g.
  // category="fashion-men") surface here too, not just legacy suits/shirts.
  const apiCats = useMemo(() => categoryProductSlugs(id), [id]);

  const catSchema = useMemo(() => {
    if (!cat) return null;
    const itemListElement = [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
      { "@type": "ListItem", "position": 2, "name": cat.name, "item": `${BASE_URL}/category/${id}` },
    ];
    if (activeSub) {
      itemListElement.push({
        "@type": "ListItem", "position": 3, "name": activeSub.name,
        "item": `${BASE_URL}/category/${id}?sub=${activeSub.slug}`,
      });
    }
    return { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": itemListElement };
  }, [cat, id, activeSub]);

  useSEO({
    // "{Category} – Shop Online in Kenya | Emporia" — useSEO appends the
    // "| Emporia". The same title is built by the Netlify edge function for
    // crawlers that never run this code (netlify/edge-functions/seo.js), so
    // the two must stay in step; categoryMeta() below is shared by both.
    title: cat ? categoryTitle(cat, activeSub) : "Category",
    description: cat ? categoryDescription(cat, activeSub, visibleChildren) : undefined,
    path: activeSub ? `/category/${id}?sub=${activeSub.slug}` : `/category/${id}`,
    schema: categoryError || productError ? null : catSchema,
    noindex: !!cat?.requires_auth || cat?.launch_active === false || (!catLoading && !categoryError && !cat) || (!!cat && !loading && !products.length && !productError && !categoryError),
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setProducts([]);
    setProductError(false);
    Promise.all(
      apiCats.map(c => {
        const base = subSlug ? `category=${c}&subcategory=${subSlug}` : `category=${c}`;
        const qs   = `${base}&sort=${sort}`;
        return cachedFetch(`${API}/products?${qs}`);
      })
    ).then(results => {
      if (!cancelled) { setProducts(results.flat()); setLoading(false); }
    }).catch(() => { if (!cancelled) { setProductError(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, [id, apiCats, subSlug, sort, retryVersion]);

  // While loading, or when filtered to a subcategory, show the products
  // section (with its own empty state) rather than the "Coming Soon" panel.
  const hasProducts = loading || products.length > 0 || !!activeSub;

  useEffect(() => {
    if (cat) trackCategoryView(id);
  }, [id, cat]);

  /* Auth-gated categories — bounce to login, then back here. Age-restricted
     ones (Drinks) instead explain why, below (owner, 2026-10-26). */
  useEffect(() => {
    if (cat?.requires_auth && !cat.age_restricted && !authLoading && !user) {
      navigate(`/login?redirect=/category/${id}`, { replace: true });
    }
  }, [cat, authLoading, user, id, navigate]);

  /* Still resolving category metadata */
  if (catLoading) return (
    <main className="cat-page">
      <SkeletonGrid count={8} className="cat-products-grid" />
    </main>
  );

  if (categoryError) return (
    <main className="cat-page"><div className="cat-not-found"><h1>Temporarily unavailable</h1><p>We could not load this category. Please try again.</p><button type="button" onClick={() => setRetryVersion((value) => value + 1)}>Retry</button></div></main>
  );

  /* 404 — unknown category */
  if (!cat) return (
    <main className="cat-page">
      <div className="cat-not-found">
        <span className="cat-nf-icon">🔍</span>
        <h1>Category not found</h1>
        <p>The category you're looking for doesn't exist.</p>
        <Link to="/" className="btn-back-home">← Back to Home</Link>
      </div>
    </main>
  );

  /* Age-restricted and signed out: say why, and offer both ways in. */
  if (cat.requires_auth && cat.age_restricted && !authLoading && !user) {
    const back = encodeURIComponent(`/category/${id}`);
    return (
      <main className="cat-page">
        <div className="cat-not-found cat-adults-only">
          <h1>{cat.name}</h1>
          <p>Drinks are for customers aged 18 and over. Sign in or create an account to see them.</p>
          <div className="cat-adults-actions">
            <Link to={`/login?redirect=${back}`} className="btn-back-home">Sign in</Link>
            <Link to="/register" className="cat-adults-secondary">Create an account</Link>
          </div>
        </div>
      </main>
    );
  }

  /* Auth required — redirect handled in the effect above */
  if (cat.requires_auth && (authLoading || !user)) return null;

  /* Age verification — alcoholic-drinks categories */
  if (cat.age_restricted && !isAgeVerifiedAdult(user) && !ageJustConfirmed) return (
    <main className="cat-page">
      <AgeGate onConfirm={() => setAgeJustConfirmed(true)} user={user} />
    </main>
  );

  if (productError) return (
    <main className="cat-page"><div className="cat-not-found"><h1>Temporarily unavailable</h1><p>We could not load products in this category. Please try again.</p><button type="button" onClick={() => setRetryVersion((value) => value + 1)}>Retry</button></div></main>
  );

  return (
    <main className="cat-page">

      {/* ── Compact hero ── */}
      <section className="cat-hero" style={{ "--cc": cat.color, "--cb": cat.bg }}>
        <div className="cat-hero-inner">
          <span className="cat-hero-icon">{cat.icon}</span>
          <div className="cat-hero-text">
            <h1 className="cat-hero-title">{activeSub ? activeSub.name : cat.name}</h1>
            <p className="cat-hero-tagline">
              {activeSub ? `${cat.name} — ${activeSub.name}` : (COMING_SOON_TAGLINES[id] || `Discover our ${cat.name} collection`)}
            </p>
          </div>
        </div>
        <div className="cat-breadcrumb">
          <Link to="/">Home</Link>
          <span>›</span>
          {activeSub ? <Link to={`/category/${id}`}>{cat.name}</Link> : <span>{cat.name}</span>}
          {activeSub && (<><span>›</span><span>{activeSub.name}</span></>)}
        </div>
      </section>

      {/* ── Subcategory chips — launch_active only ── */}
      <Reveal as="section" className="cat-subs-section reveal">
        <p className="cat-subs-label">Browse subcategories</p>
        <div className="cat-subs">
          {visibleChildren.map(child => {
            const active = child.slug === subSlug;
            return (
              <Link
                key={child.slug}
                to={active ? `/category/${id}` : `/category/${id}?sub=${child.slug}`}
                className={`cat-sub-chip${active ? " active" : ""}`}
                onClick={() => trackFilterUse(cat.name, child.name)}
              >
                {child.name}
              </Link>
            );
          })}
        </div>
      </Reveal>

      {/* ── Products (if available) ── */}
      {hasProducts && (
        <section className="cat-products-section">
          <div className="cat-products-header">
            <div className="cat-products-header-left">
              <h2>{activeSub ? `${cat.name} — ${activeSub.name}` : cat.name}</h2>
              {!loading && <span className="cat-count">{products.length} items</span>}
            </div>
            <select
              className="cp-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              aria-label="Sort products"
            >
              <option value="newest">Newest</option>
              <option value="price_asc">Price: Low–High</option>
              <option value="price_desc">Price: High–Low</option>
            </select>
          </div>
          {loading
            ? <SkeletonGrid count={8} className="cat-products-grid" />
            : products.length > 0
              ? (
                <Reveal as="div" className="cat-products-grid reveal">
                  {products.map((p, i) => (
                    <div key={p.id} className="product-card-anim" style={{ "--i": i }}>
                      <ProductCard product={p} onAddToCart={onAddToCart} />
                    </div>
                  ))}
                </Reveal>
              )
              : <div className="cat-empty-state">
                  <span>📦</span>
                  <p>No products listed yet in this subcategory.</p>
                </div>
          }
        </section>
      )}

      {/* ── Coming Soon (categories without products) ── */}
      {!hasProducts && (
        <Reveal as="section" className="cat-coming-soon reveal">
          <div className="ccs-icon">{cat.icon}</div>
          <h2>Coming Soon</h2>
          <p>
            We're onboarding verified sellers for <strong>{cat.name}</strong>.
            Subscribe to be notified the moment new products go live.
          </p>
          {!notified ? (
            <form className="ccs-form" onSubmit={e => { e.preventDefault(); setNotified(true); }}>
              <input type="email" placeholder="Your email address" required />
              <button type="submit">Notify Me</button>
            </form>
          ) : (
            <div className="ccs-confirmed">✓ You're on the list — we'll notify you soon!</div>
          )}

          {/* Related categories */}
          <div className="ccs-related">
            <p className="ccs-related-label">Explore related categories</p>
            <div className="ccs-related-grid">
              {allCats.filter(c => c.slug !== id).slice(0, 6).map(c => (
                <Link key={c.slug} to={`/category/${c.slug}`} className="ccs-related-card">
                  <span style={{ background: c.bg, borderRadius: "8px", padding: "0.4rem 0.5rem" }}>{c.icon}</span>
                  <span>{c.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      {/* ── All categories quick browse ── */}
      <section className="cat-all-section">
        <h3>All Categories</h3>
        <Reveal as="div" className="cat-all-grid reveal-children">
          {allCats.map((c, i) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className={`cat-all-item${c.slug === id ? " active" : ""}`}
              style={{ "--cc": c.color, "--cb": c.bg, "--i": i }}
            >
              <span className="cai-name">{c.name}</span>
            </Link>
          ))}
        </Reveal>
      </section>

    </main>
  );
};

export default CategoryPage;
